import ExcelJS from "exceljs";
import dayjs from "dayjs";
import {
  DashboardSummary,
  ModelStatus,
  ProductionTrendPoint,
  TrendUnit,
} from "../api/dashboard";

const TREND_UNIT_LABEL: Record<TrendUnit, string> = {
  day: "일별",
  month: "월별",
  year: "연별",
};

const downloadWorkbook = async (workbook: ExcelJS.Workbook, fileName: string) => {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(link.href);
};

const styleHeaderRow = (row: ExcelJS.Row) => {
  row.font = { bold: true, color: { argb: "FFFFFFFF" } };
  row.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF4C6EF5" },
    };
    cell.alignment = { vertical: "middle", horizontal: "center" };
    cell.border = {
      top: { style: "thin", color: { argb: "FFDDDDDD" } },
      bottom: { style: "thin", color: { argb: "FFDDDDDD" } },
      left: { style: "thin", color: { argb: "FFDDDDDD" } },
      right: { style: "thin", color: { argb: "FFDDDDDD" } },
    };
  });
};

interface ExportParams {
  summary: DashboardSummary | null;
  modelStatus: ModelStatus[];
  trend: ProductionTrendPoint[];
  trendUnit: TrendUnit;
  vanLabelMap: Record<string, string>;
}

export const exportDashboardExcel = async ({
  summary,
  modelStatus,
  trend,
  trendUnit,
  vanLabelMap,
}: ExportParams) => {
  const workbook = new ExcelJS.Workbook();
  const now = dayjs();

  // 1) 요약 시트
  const summarySheet = workbook.addWorksheet("요약");
  summarySheet.columns = [
    { header: "항목", key: "label", width: 24 },
    { header: "값", key: "value", width: 18 },
  ];
  styleHeaderRow(summarySheet.getRow(1));
  const summaryRows: Array<[string, string | number]> = summary
    ? [
        ["전체 등록 단말기", summary.totalCount],
        ["미주입", summary.notInjectedCount],
        ["주입완료(OK)", summary.injectedCount],
        ["완료율(%)", summary.completionRate],
        ["오늘 주입 성공", summary.todayInjectSuccess],
        ["오늘 주입 실패", summary.todayInjectFail],
        ["기준 시각", now.format("YYYY-MM-DD HH:mm:ss")],
      ]
    : [["데이터 없음", "-"]];
  summaryRows.forEach(([label, value]) =>
    summarySheet.addRow({ label, value }),
  );

  // 2) 모델별 현황 시트
  const modelSheet = workbook.addWorksheet("모델별 현황");
  modelSheet.columns = [
    { header: "내부모델", key: "internalModelName", width: 18 },
    { header: "외부모델", key: "externalModelName", width: 18 },
    { header: "VAN", key: "vanName", width: 14 },
    { header: "총 수량", key: "totalCount", width: 12 },
    { header: "미주입", key: "notInjectedCount", width: 12 },
    { header: "주입완료", key: "injectedCount", width: 12 },
    { header: "완료율(%)", key: "rate", width: 12 },
  ];
  styleHeaderRow(modelSheet.getRow(1));
  modelStatus.forEach((m) => {
    const rate =
      m.totalCount > 0
        ? Math.round((m.injectedCount / m.totalCount) * 1000) / 10
        : 0;
    modelSheet.addRow({
      internalModelName: m.internalModelName,
      externalModelName: m.externalModelName,
      vanName: vanLabelMap[String(m.vanType)] ?? m.vanType,
      totalCount: m.totalCount,
      notInjectedCount: m.notInjectedCount,
      injectedCount: m.injectedCount,
      rate,
    });
  });

  // 3) 생산량 추이 시트
  const trendSheet = workbook.addWorksheet(`생산량 추이(${TREND_UNIT_LABEL[trendUnit]})`);
  trendSheet.columns = [
    { header: "구간", key: "period", width: 18 },
    { header: "생산량(주입 성공)", key: "count", width: 18 },
  ];
  styleHeaderRow(trendSheet.getRow(1));
  trend.forEach((t) => trendSheet.addRow({ period: t.period, count: t.count }));

  await downloadWorkbook(
    workbook,
    `대시보드_리포트_${now.format("YYYY-MM-DD_HH-mm-ss")}.xlsx`,
  );
};
